'use strict';

// External libraries
import classNames from 'classnames';
import indexBy from 'lodash.indexby';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Component } from 'react';
import Helmet from 'react-helmet';
import Modal from 'react-modal';
import { Link } from 'react-router';
import uuid from 'uuid';

// Components
import RecommendModal from '@app/pro/components/Modals/RecommendModal.react';
import MealTitle from '@components/Meals/MealTitle.react';
import RecipeIngredients from '@components/Modules/RecipeIngredients.react';
import RecipeInstructions from '@components/Modules/RecipeInstructions.react';
import ConfirmOverwriteModal from '@components/Planner/Modals/ConfirmOverwriteModal.react';
import LeftoverDatesModal from '@components/Planner/Modals/LeftoverDatesModal.react';
import NutritionInfo from '@components/Products/NutritionInfo.react';
import AddToCollectionButton from '@components/Recipes/AddToCollectionButton.react';
import EditMealServingsModalPrimary from '@components/Recipes/EditMealServingsModalPrimary.react';
import EditMealServingsModalSecondary from '@components/Recipes/EditMealServingsModalSecondary.react';
import RecipeEditor from '@components/Recipes/Editor.react';
import HowMuchToEat from '@components/Recipes/HowMuchToEat.react';
import Merchant from '@components/Recipes/Merchant.react';
import RecipeToPdfButton from '@components/Recipes/RecipeToPdfButton.react';
import SchedulingModal from '@components/Recipes/SchedulingModal.react';
import FavoriteButton from '@components/Widgets/FavoriteButton.react';
import Footer from '@components/Widgets/footer/Footer.react';
import ImgResized from '@components/Widgets/ImgResized.react';
import LoginForm from '@components/Widgets/LoginForm.react';
import Popup from '@components/Widgets/Popup.react';
import SharePopup from '@components/Widgets/SharePopup.react';

// Actions & Stores
import GroceryActions from '@actions/GroceryActions';
import MealActions from '@actions/MealActions';
import MealStore from '@stores/MealStore';
import PlanStore from '@stores/PlanStore';
import UserStore from '@stores/UserStore';

// Utils
import Analytics from '@utils/Analytics';
import { fetchDocumentsById } from '@utils/Content';
import { toHuman } from '@utils/Duration';
import { inquire } from '@utils/Enforcer';
import { getConfig } from '@utils/Env';
import { isMealInGroceries } from '@utils/Grocery';
import {
    getAssetsForMeals,
    getBatchInfo,
    getDefaultParticipantsForRecipe,
    shouldCreateLeftovers,
    getLoggedServingsOfRecipe,
    getRescheduleOverlaps,
    normalizeParticipantsToArray,
    updateMealLeftovers,
} from '@utils/Meals';
import { getBatchInfo as getBatchInfoProMeals } from '@utils/proMeals';
import { getNutrientsToDisplay } from '@app/pro/utils/Patients';
import { ScreenService } from '@utils/ScreenService';

// Data
import nutrKeys from '@app/tables/nutrient-order';

// Styles
import '@app/pages/MealDetails.scss';

export default class RecipeDetails extends Component {
    static propTypes = {
        uuid: PropTypes.string,
        recipe: PropTypes.object,

        editableLeftovers: PropTypes.bool,
        editableParticipants: PropTypes.bool,
        hideScheduleButton: PropTypes.bool,
        hidePrintBtn: PropTypes.bool,

        defaultScaling: PropTypes.number,
        defaultParticipants: PropTypes.array,
        defaultLoggedPortion: PropTypes.number,

        onChangeParticipants: PropTypes.func,
        onRecipeUpdated: PropTypes.func,
        sourceMetaData: PropTypes.object,
    };

    static defaultProps = {
        editableLeftovers: true,
        editableParticipants: true,
    };

    static contextTypes = {
        router: PropTypes.object,
        location: PropTypes.object,
        isMobile: PropTypes.bool,
        isPro: PropTypes.bool,
        showUpgradeForm: PropTypes.func,
        showLoginForm: PropTypes.func,
        addSwapContext: PropTypes.object,
    };

    static childContextTypes = {
        foods: PropTypes.object,
        meals: PropTypes.array,
        user: PropTypes.object,
    };

    constructor(props, context) {
        super(props, context);

        const user = UserStore.getUser();
        const profile = this.getProfileFromProps(props, context, user);
        const meals = MealStore.getMeals();
        const plans = PlanStore.getPlans();

        this.state = {
            loading: false,
            synced: false,
            user,
            profile,
            plans,
            participants: null,
            scaling: null,
            allMeals: meals,
            dirty: false,

            isEditModalOpen: false,
            leftoverParams: null,
            pendingLeftovers: null,
            hasEditedLeftovers: false,
        };
    }

    getChildContext = () => {
        const { foods = {}, allMeals = [], user, profile } = this.state;

        return {
            foods,
            meals: allMeals,
            user: user || profile, // Provide user or profile (which is user object) to child components
        };
    };

    componentDidMount = () => {
        const { recipe, uuid } = this.props;

        ScreenService.keepAwake();

        if (recipe?.uuid) {
            this.loadRecipe(recipe.uuid);
        } else if (uuid) {
            this.loadRecipe(this.props.uuid);
        }

        UserStore.addChangeListener(this.onUserStoreChange);
        MealStore.addChangeListener(this.onMealStoreChange);
        PlanStore.addChangeListener(this.onPlanStoreChange);
    };

    componentWillUnmount = () => {
        ScreenService.allowSleep();
        UserStore.removeChangeListener(this.onUserStoreChange);
        MealStore.removeChangeListener(this.onMealStoreChange);
        PlanStore.removeChangeListener(this.onPlanStoreChange);
    };

    onUserStoreChange = () => {
        this.setState({ user: UserStore.getUser() });
    };

    onMealStoreChange = () => {
        const meals = MealStore.getMeals();
        this.setState({ allMeals: meals });
    };

    onPlanStoreChange = () => {
        const plans = PlanStore.getPlans();
        this.setState({ plans });
    };

    syncAssets = () => {
        let { recipe, profile, participants, scaling, translated, allMeals } = this.state;
        const { defaultParticipants, defaultScaling } = this.props;

        if (!participants) {
            participants = defaultParticipants || getDefaultParticipantsForRecipe(recipe, profile);

            const needed = participants.reduce((total, member) => total + (member.portion || 1), 0);
            scaling = defaultScaling || Math.ceil(needed / (recipe.servings || 1));
        }

        this.setState({ participants, scaling });

        let uuidsToLoad = [recipe.details];

        if (recipe.merchant) {
            uuidsToLoad.push(recipe.merchant.uuid);
        }

        uuidsToLoad = uuidsToLoad.concat(recipe.subrecipes.map((sr) => sr.recipe));
        uuidsToLoad = uuidsToLoad.concat(recipe.subrecipes.map((sr) => sr.details));
        uuidsToLoad = uuidsToLoad.concat(recipe.foods || []);

        getAssetsForMeals(allMeals).then(({ recipes, foods, details, groceries }) => {
            this.setState({
                allRecipes: recipes,
                allFoods: foods,
                allDetails: details,
                allGroceries: groceries,
                synced: true,
            });
        });

        return fetchDocumentsById(uuidsToLoad).then(async (documents) => {
            let details = documents.find((d) => d.uuid === recipe.details);
            const merchant = documents.filter((d) => d.type === 'merchant')[0];
            const subrecipes = documents.filter((d) => d.type === 'recipe');
            const subdetails = indexBy(
                documents.filter((d) => d.type === 'recipe_details'),
                'uuid'
            );
            const foods = indexBy(
                documents.filter((d) => d.type === 'food'),
                'uuid'
            );

            if (!details) {
                this.setState({ error: 'Details not found', synced: true, loading: false });
                return;
            }

            this.setState({
                recipe,
                details,
                subdetails,
                subrecipes,
                foods,
                merchant,
                translated,
                synced: true,
                loading: false,
            });
        });
    };

    loadRecipe = (uuid) => {
        let { profile } = this.state;

        this.setState({ loading: true });

        // First, inquire to see if we have write and publish permission.
        const inquiries = [
            { action: 'publish', resource: uuid },
            { action: 'write', resource: uuid },
        ];

        inquire(inquiries).then((decisions) => {
            if (!decisions) {
                this.setState({ canWrite: false });
                return;
            }

            let canPublish = decisions.filter((d) => d.action == 'publish')[0].decision === 'permit';
            let canWrite = decisions.filter((d) => d.action == 'write')[0].decision === 'permit';

            this.setState({ canPublish, canWrite });
        });

        // Second, asynchronously load the recipe
        return fetchDocumentsById([uuid]).then(async (recipes) => {
            if (!recipes || (recipes && !recipes[0])) {
                this.setState({ error: 'Recipe not found' });
                return;
            }

            const recipe = recipes[0];

            this.setState({ recipe }, this.syncAssets);

            Analytics.viewRecipe({
                'Recipe UUID': recipe.uuid,
                'Recipe Name': recipe.title,
            });
        });
    };

    getProfileFromProps = (props, context, user) => {
        if (context.profile) {
            return context.profile;
        }

        if (context.addSwapContext && context.addSwapContext.profile) {
            return context.addSwapContext.profile;
        }

        if (!user) {
            return {
                preferences: { diets: [], avoidances: [] },
                conditions: ['General Healthy Diet'],
                family: [],
                portion: 1,
            };
        }

        let {
            preferences = {diets: [], avoidances: []},
            conditions = ['General Healthy Diet'],
            family = [],
            portion = 1,
            hide_nutrition = false,
            ...rest
        } = user;

        return {
            preferences,
            conditions,
            family,
            portion,
            ...rest,
        };
    };

    openRecipeEditModal = () => {
        this.setState({ isEditModalOpen: true });
    };

    openRecipeDuplicateModal = () => {
        this.setState({ isEditModalOpen: true, saveAsCopy: true });
    };

    closeRecipeEditModal = () => {
        this.setState({ isEditModalOpen: false });
    };

    closeParticipantModal = () => {
        this.setState({ isEditingParticipants: false });
    };

    showEditMealServings = () => {
        const { editableLeftovers } = this.props;
        const { user } = this.state;

        // Don't open the edit dialog if the user is not logged in.
        if (!editableLeftovers || !user) {
            return;
        }

        this.setState({ isEditingParticipants: true });
    };

    onChangeParticipants = (participants, scaling, leftoverDates, replaceMeals = [], editLeftovers = false, returnLeftoversOnly = false, extraServings = 0, dishes = []) => {
        const { onChangeParticipants, recipe } = this.props;

        // If editLeftovers is true, handle differently based on context
        if (editLeftovers) {
            const { addSwapContext } = this.context;

            // In add swap context, we need to show leftover modal immediately
            // because there's no scheduling flow - the meal replacement happens directly
            if (addSwapContext) {
                const { user, allMeals = [] } = this.state;

                // Create a proper meal object matching the structure expected by LeftoverDatesModal
                // This needs to match real meal objects from the meals array
                const meal = {
                    uuid: uuid.v4(), // Generate a temporary UUID for this meal
                    recipe_uuid: recipe?.uuid,
                    meal: 'Dinner', // Default meal type
                    date: moment().format('YYYY-MM-DD'), // Current date
                    participants: participants.map(p => p.uuid || '').join(','), // Comma-separated string, not array
                    scaling: scaling,
                    extra_servings: extraServings || 0
                };

                // Check if leftovers should be created
                const loggedServings = getLoggedServingsOfRecipe(meal, recipe);

                if (shouldCreateLeftovers(recipe, scaling, participants, user, loggedServings)) {
                    // Update state but keep the edit modal open (don't set isEditingParticipants to false)
                    this.setState({
                        participants,
                        scaling,
                        dirty: true,
                        hasEditedLeftovers: editLeftovers,
                        leftoverParams: {
                            participants,
                            scaling,
                            meal,
                            returnLeftoversOnly: returnLeftoversOnly,
                            extraServings: extraServings || 0
                        }
                    });
                    return;
                }
            }
            // In scheduling flow, just store the state - leftover modal will appear during addToCalendar
            // (See addToCalendar method line 643-664 which already handles this flow)
        }

        // Store state and close the edit modal
        this.setState({
            participants,
            scaling,
            isEditingParticipants: false,
            dirty: true,
            hasEditedLeftovers: editLeftovers
        });

        // For save (not scheduling), just update the state without leftover logic
        onChangeParticipants && onChangeParticipants(participants, scaling, leftoverDates);
    };

    onChangeScaling = (scaling, dirty) => {
        const { onChangeParticipants } = this.props;

        // Store state and close the edit modal
        this.setState({
            scaling,
            isEditingParticipants: false,
            dirty: dirty
        });

        // Propagate to parent if callback exists
        onChangeParticipants && onChangeParticipants(this.state.participants, scaling, null);
    };

    openScheduleModal = () => {
        this.setState({ isScheduling: true });
    };

    closeScheduleModal = () => {
        this.setState({ isScheduling: false });
    };

    closeLeftoverDatesModal = () => {
        this.setState({ leftoverParams: null });
    };

    onSelectLeftoverDates = (leftovers, scalingMap, extraServings = 0) => {
        const { leftoverParams } = this.state;

        if (leftoverParams && leftoverParams.schedulingDate && leftoverParams.schedulingMealType) {
            // Complete the scheduling with leftovers
            this.setState({ leftoverParams: null }, () => {
                const { meal, schedulingDate, schedulingMealType } = leftoverParams;

                // Apply extraServings to the meal if specified
                let mealToSchedule = meal;
                if (extraServings > 0) {
                    mealToSchedule = {
                        ...meal,
                        extra_servings: (meal.extra_servings || 0) + extraServings
                    };
                }

                this.completeMealScheduling(mealToSchedule, schedulingDate, schedulingMealType, leftovers);
            });
        } else {
            // Coming from edit meal servings modal (not scheduling)
            // Store the leftovers as pending and close the leftover modal
            // The edit modal will receive the pending leftovers and update its display
            this.setState({
                leftoverParams: null,
                pendingLeftovers: {
                    leftovers,
                    scalingMap,
                    extraServings
                }
            });
        }
    };

    completeMealScheduling = (meal, date, mealType, leftovers = null) => {
        const { profile, allMeals, allRecipes, allFoods, plans } = this.state;
        const { router } = this.context;

        const dateFormatted = date.format('YYYY-MM-DD');
        // Create all meals to schedule (main + leftovers)
        let mealsToSchedule = [meal];
        let leftoverMeals = [];

        if (leftovers && leftovers.length > 0) {
            // Create leftover meals from the leftover dates selection
            leftoverMeals = leftovers.map(leftover => ({
                ...meal, // Copy main meal properties
                uuid: uuid.v4(), // New UUID for leftover
                meal_type: 'leftover',
                parent_uuid: meal.uuid,
                date: leftover.date || leftover.leftoverDate?.format('YYYY-MM-DD'),
                meal: leftover.mealType,
                participants: leftover.participants?.map(p => p.uuid || '').join(',') || meal.participants,
                created: moment().format(),
            }));
        }


        // Create overlaps manually for all meals (main + leftovers) being scheduled
        let overlaps = [], clears = [];

        // Check main meal conflict
        const mainMealConflicts = allMeals.filter(m => !m.deleted &&
            m.meal === mealType &&
            date.isSame(m.date, 'day'));

        if (mainMealConflicts.length) {
            overlaps.push({
                mealType,
                date,
                destMeals: mainMealConflicts,
                sourceMeals: [meal], // Just the main meal
                participants: normalizeParticipantsToArray(meal.participants)
            });
        } else {
            clears.push({
                mealType,
                date,
                sourceMeals: [meal],
                participants: normalizeParticipantsToArray(meal.participants)
            });
        }

        // Check leftover meal conflicts if leftovers exist
        if (leftovers && leftovers.length > 0) {
            leftoverMeals.forEach(leftoverMeal => {
                const leftoverConflicts = allMeals.filter(m => !m.deleted &&
                    m.meal === leftoverMeal.meal &&
                    moment(leftoverMeal.date).isSame(m.date, 'day'));

                if (leftoverConflicts.length) {
                    overlaps.push({
                        mealType: leftoverMeal.meal,
                        date: moment(leftoverMeal.date),
                        destMeals: leftoverConflicts,
                        sourceMeals: [leftoverMeal],
                        participants: normalizeParticipantsToArray(leftoverMeal.participants)
                    });
                } else {
                    clears.push({
                        mealType: leftoverMeal.meal,
                        date: moment(leftoverMeal.date),
                        sourceMeals: [leftoverMeal],
                        participants: normalizeParticipantsToArray(leftoverMeal.participants)
                    });
                }
            });
        }

        // Common list of all meals to be scheduled (main + leftovers)
        const allMealsToSchedule = leftovers && leftovers.length > 0 ?
            [meal].concat(leftoverMeals) : [meal];
        if (!overlaps.length) {
            MealActions.upsertMeals(allMealsToSchedule);
            router.push({ pathname: `/meals/${dateFormatted}/${mealType}` });
        } else {
            this.setState({
                isConfirmOverwriteOpen: true,
                mealsToReschedule: allMealsToSchedule,
                confirmOverlaps: overlaps,
                confirmClears: clears,
                confirmDate: date,
                confirmMealType: mealType,
            });
        }
    };

    onCompleteSignIn = (user) => {
        const { date, mealType } = this.state;

        this.addToCalendar(date, mealType);
    };

    onCompleteSignUp = (user) => {
        const { date, mealType } = this.state;

        this.addToCalendar(date, mealType);
    };

    addToCalendar = (date, mealType = 'Dinner') => {
        const { user, profile, recipe, participants, scaling, allMeals, allRecipes, allFoods, plans} = this.state;
        const { location, router, showLoginForm, showUpgradeForm } = this.context;

        if (!recipe) {
            return false;
        }

        if (!user) {
            // Did the user come from an email or google?
            const loginFormOpts = {
                defaultMode:
                    location &&
                    location.query &&
                    (location.query.utm_source == 'rd_recommendation' || location.query.showLogin == '1')
                        ? 'sign-in'
                        : 'sign-up',
                signupLinkSwitchesModes: true,
                onCompleteSignUp: this.onCompleteSignUp,
                onCompleteSignIn: this.onCompleteSignIn,
                signUpProfile: {},
                offModalCta: 'You are one more step away from finding delicious meals just for you.',
            };

            // Store our date for later
            this.setState({ date, mealType });
            showLoginForm && showLoginForm(loginFormOpts);
            return;
        }

        const { capabilities = {} } = user;

        if (recipe.protection !== 'public' && !capabilities.meal_planner) {
            showUpgradeForm({ feature: 'meal_planner' });

            return false;
        }

        let needed = participants.reduce((total, member) => total + member.portion, 0);
        let dateFormatted = date.format('YYYY-MM-DD');

        let meal = {
            uuid: uuid.v4(),
            meal: mealType,
            meal_type: 'fresh',
            logged_amount: profile.portion,
            logged_unit: 'serving',
            logged_grams: (Math.round(recipe.grams_per_serving * 1000) / 1000) * profile.portion,
            logged_milliliters: (Math.round(recipe.milliliters_per_serving * 1000) / 1000) * profile.portion,
            recipe_uuid: recipe.uuid,
            details_uuid: recipe.details,
            recipe_title: recipe.title,
            recipe_image: recipe.image,
            date: dateFormatted,
            created: moment().format(),
            participants: participants.map((p) => p.uuid || '').join(','),
            scaling,
        };

        const query = {};

        // Check if we need to handle leftovers first
        if (this.state.hasEditedLeftovers && recipe && ['recipe', 'food'].includes(recipe.type)) {
            const loggedServings = getLoggedServingsOfRecipe({ recipe_uuid: recipe.uuid }, recipe);

            if (shouldCreateLeftovers(recipe, scaling, participants, user, loggedServings)) {
                this.setState({
                    leftoverParams: {
                        participants,
                        scaling,
                        meal,
                        returnLeftoversOnly: false,
                        schedulingDate: date,
                        schedulingMealType: mealType
                    }
                });
                return;
            }
        }

        const { overlaps, clears } = getRescheduleOverlaps(profile, allMeals, allRecipes, allFoods, plans, [meal], date, mealType);

        if (!overlaps.length) {
            MealActions.upsertMeals([meal]);
            router.push({ pathname: `/meals/${dateFormatted}/${mealType}` });
        } else {
            this.setState({
                isConfirmOverwriteOpen: true,
                mealsToReschedule: [meal],
                confirmOverlaps: overlaps,
                confirmClears: clears,
                confirmDate: date,
                confirmMealType: mealType,
            });
        }

        return true;
    };

    closeConfirmOverwriteModal = () => {
        this.setState({
            isConfirmOverwriteOpen: false,
            confirmOverlaps: null,
            confirmClears: null,
            confirmDate: null,
            confirmMealType: null,
        });
    };

    renderConfirmOverwriteModal = () => {
        const {
            isConfirmOverwriteOpen,
            mealsToReschedule,
            confirmOverlaps,
            confirmClears,
            confirmDate,
            confirmMealType,
        } = this.state;

        if (!isConfirmOverwriteOpen || !mealsToReschedule) {
            return null;
        }

        return (
            <ConfirmOverwriteModal
                meals={mealsToReschedule}
                onModifyMeals={this.onModifyMeals}
                overlaps={confirmOverlaps}
                isRepeatMeal={false}
                clears={confirmClears}
                date={confirmDate}
                mealType={confirmMealType}
                closeModal={this.closeConfirmOverwriteModal}
                isRecipeSearch={true}
            />
        );
    };

    onModifyMeals = (mealsToUpsert, mealsToDelete = [], leftoverDates = null) => {
        let { synced, allGroceries, allMeals, allRecipes, allDetails, allFoods, confirmDate, confirmMealType } =
            this.state;
        const { router } = this.context;
        const { user } = this.state;

        // Do not do anything if we are not synced yet
        if (!synced) {
            return;
        }

        mealsToDelete = mealsToDelete || [];
        let grocerySyncMeals = [],
            upsertLeftovers = [];

        const upsertIds = mealsToUpsert.map((m) => m.uuid);

        mealsToUpsert.forEach((meal) => {
            delete meal.deleted;

            if (leftoverDates && meal.meal_type === 'fresh') {
                // Next update the leftovers.
                const { leftovers, toRemove } = updateMealLeftovers(
                    meal,
                    allMeals,
                    allRecipes[meal.recipe_uuid],
                    user,
                    leftoverDates
                );

                // Append the updated leftovers, and any leftovers that need to be removed, to our working lists.
                upsertLeftovers = upsertLeftovers.concat(leftovers);
                mealsToDelete = mealsToDelete.concat(toRemove);
            }

            // If this meal is already in the groceries and needs to be synchronized, sync it.
            if (isMealInGroceries(meal, allGroceries) && meal.scaling != meal.scaling_added_to_groceries) {
                grocerySyncMeals.push(meal);
            }
        });

        if (grocerySyncMeals.length > 0) {
            GroceryActions.syncMealsToGroceries(grocerySyncMeals, allRecipes, allDetails, allFoods);
        }

        if (mealsToDelete && mealsToDelete.length > 0) {
            // Signal to the grocery list to remove ingredients too
            GroceryActions.removeMealsFromGroceries(mealsToDelete, allRecipes, allDetails, allFoods);

            // Collect any leftovers for these meals as well.
            const uuidsToDelete = mealsToDelete.map((m) => m.uuid);
            const childrenToDelete = allMeals.filter((m) => uuidsToDelete.includes(m.parent_uuid));
            mealsToDelete = mealsToDelete.concat(childrenToDelete);

            // Mark all meals as deleted
            mealsToDelete.forEach((m) => (m.deleted = true));

            // Signal the Meal Store to remove the fresh meal & the leftovers
            MealActions.deleteMeals(mealsToDelete);
        }

        MealActions.upsertMeals(mealsToUpsert.concat(upsertLeftovers));

        const dateFormatted = confirmDate.format('YYYY-MM-DD');

        router.push({ pathname: `/meals/${dateFormatted}/${confirmMealType}` });
    };

    renderCallToAction = () => {
        const { onSelectRecipe, hideScheduleButton, sourceMetaData } = this.props;
        const { user, recipe, participants, scaling } = this.state;
        const { addSwapContext } = this.context;
        const { capabilities = {} } = user || {};
        const isLogging = addSwapContext && addSwapContext.auto_log ? true : false;

        const content = [];

        if (onSelectRecipe) {
            return (
                <button
                    className="el-toolbar-green-pill-btn"
                    onClick={() => onSelectRecipe(recipe, participants, scaling, sourceMetaData)}
                >
                    {isLogging ? 'log meal' : 'select recipe'}
                    {recipe?.protection !== 'public' && !capabilities.meal_planner ? <i className="icon-lock" /> : null}
                </button>
            );
        }

        if (hideScheduleButton) {
            return;
        }

        return (
            <button className="el-toolbar-green-pill-btn" onClick={this.openScheduleModal}>
                schedule it
                {recipe?.protection !== 'public' && !capabilities.meal_planner ? <i className="icon-lock" /> : null}
            </button>
        );
    };

    onStartRecommend = (recipe_uuid) => {
        const { location, router } = this.context;
        const { pathname, hash, query } = location;

        query.recommend = recipe_uuid;

        router.push({ pathname, hash, query });
    };

    closeRecommendModal = (sent) => {
        const { location, router } = this.context;
        const { pathname, hash, query } = location;

        delete query.recommend;

        router.push({ pathname, hash, query });
    };

    renderRecommendModal = () => {
        const { location } = this.context;
        const { recipe } = this.state;

        if (!location.query.recommend) {
            return;
        }

        return <RecommendModal recipe={recipe} closeModal={this.closeRecommendModal} />;
    };

    renderControlBar = () => {
        const { user, recipe, canPublish, scaling } = this.state;
        const { hidePrintBtn } = this.props;
        const { isPro, isMobile } = this.context;

        if (!recipe) {
            return;
        }

        return (
            <div className={classNames('control-bar-container el-toolbar1', !isMobile ? 'el-toolbar1-dark' : null)}>
                {!hidePrintBtn ? (
                    <RecipeToPdfButton
                        scaling={scaling}
                        recipe={recipe}
                        className="el-toolbar-popup"
                        positionClassName="el-popup-top-left"
                        button={
                            <>
                                <i className="feather feather-printer" /> <em>print</em>
                            </>
                        }
                    />
                ) : null}

                <SharePopup className="el-toolbar-popup" recipe={recipe} />
                {isPro ? (
                    <button className="el-toolbar-btn" onClick={() => this.onStartRecommend(recipe.uuid)}>
                        <em>recommend</em>
                    </button>
                ) : null}
                {this.renderCallToAction()}
            </div>
        );
    };

    renderEditMealServingsButton = () => {
        const { user, recipe, participants, scaling } = this.state;
        const { editableLeftovers } = this.props;
        const { addSwapContext } = this.context;

        if (!recipe || !participants) {
            return;
        }

        const totalTime = recipe.total_time || 0;
        const totalParticipants = participants?.length || 1;
        const recipeYield = (recipe.servings || 1) * (scaling || 1);

        // Show "Serves X people" only in add swap context, otherwise show "Yields X servings"
        const isInSwapContext = !!addSwapContext;

        return (
            <div className="edit-meal-servings" onClick={this.showEditMealServings}>
                <section className="serves">
                    <div>
                        {isInSwapContext ? (
                            <>
                                <div>Serves</div>
                                <div className="servings">{totalParticipants} {totalParticipants === 1 ? 'person' : 'people'}</div>
                            </>
                        ) : (
                            <>
                                <div>Yields</div>
                                <div className="servings">{recipeYield} {recipeYield === 1 ? 'serving' : 'servings'}</div>
                            </>
                        )}
                    </div>

                    <div className="edit">EDIT</div>
                </section>

                <section className="timings">
                    {totalTime ? (
                        <p>
                            {toHuman(totalTime, true)}
                            <br />
                            prep/cook
                        </p>
                    ) : null}
                </section>
            </div>
        );
    };

    renderNutritionTabs = () => {
        const { profile, recipe } = this.state;

        const { hide_nutrition = false, rd_override = false } = profile || {};

        if (!(recipe && recipe.nutrients && recipe.nutrients.values)) {
            return null;
        }

        if (hide_nutrition && rd_override !== true) {
            return;
        }

        const toDisplay = getNutrientsToDisplay(profile, (recipe.nutrients && recipe.nutrients.values) || {});

        const nutrNos = Object.keys(toDisplay).sort((a, b) => {
            const aI = nutrKeys.indexOf(a),
                bI = nutrKeys.indexOf(b);

            if (aI > bI) return 1;
            if (aI < bI) return -1;
            return 0;
        });

        return (
            <section className="details-section second">
                <header>
                    <div className="section-title">
                        <p className="t2">Nutrition Information</p>
                    </div>
                </header>

                <div className="section-body">
                    <NutritionInfo nutrients={recipe.nutrients.values} profile={profile} />
                </div>
            </section>
        );
    };

    renderEditMealServingsModal = () => {
        const { recipe, profile, participants, scaling, isEditingParticipants, allMeals, dirty, pendingLeftovers } = this.state;
        const { editableParticipants } = this.props;
        const { addSwapContext, isPro } = this.context;

        if (!recipe || !isEditingParticipants) {
            return;
        }

        let inheritedParticipants = participants;
        let inheritedScaling = scaling;

        if (addSwapContext?.mode === 'replace' && addSwapContext.participants) {
            // Use participants directly from addSwapContext (already correctly set by wrapper)
            inheritedParticipants = addSwapContext.participants;
            const needed = inheritedParticipants.reduce((total, member) => total + (member.portion || 1), 0);
            inheritedScaling = Math.ceil(needed / (recipe.servings || 1));
        }

        const mealDishes = [{
            uuid: recipe?.uuid,
            recipe_uuid: recipe?.uuid,
            meal_type: 'fresh',
            scaling: dirty ? scaling : inheritedScaling
        }];

        const contents = recipe ? { [recipe.uuid]: recipe } : {};

        // Determine which modal to use based on participants
        const hasParticipants = inheritedParticipants && inheritedParticipants.length > 0;

        // Use Secondary modal when no participants (PRO meal search)
        if (!hasParticipants) {
            return (
                <EditMealServingsModalSecondary
                    closeModal={this.closeParticipantModal}
                    contents={contents}
                    mealDishes={mealDishes}
                    scaling={dirty ? scaling : inheritedScaling}
                    onChangeScaling={this.onChangeScaling}
                />
            );
        }

        // Use Primary modal when participants exist
        // - In add swap flow: Leftovers are handled by AddSwapWrapper's onSelectRecipe after recipe selection
        // - In meal search flow: We don't show leftover modal during scheduling; users can edit leftovers after scheduling the meal
        return (
            <EditMealServingsModalPrimary
                closeModal={this.closeParticipantModal}
                contents={contents}
                mealDishes={mealDishes}
                scaling={dirty ? scaling : inheritedScaling}
                participants={inheritedParticipants}
                editableParticipants={editableParticipants}
                onChangeParticipants={this.onChangeParticipants}
                profile={profile}
                hideLeftoverOptions={true}
            />
        );
    };

    renderModalBody = () => {
        const { inhibit_swap = false } = profile || {};
        const {
            recipe,
            loading,
            synced,
            user,
            profile,
            details,
            subdetails,
            merchant,
            participants,
            scaling,
            canPublish,
        } = this.state;
        const { isMobile } = this.context;
        const canEditSingleMeal = user && !inhibit_swap;
        const canDuplicateMeal = user && recipe && user.uuid == recipe.owner;

        if (loading || !synced) {
            return (
                <div className="details-loading el-fonts">
                    <h4>Loading food</h4>
                    <i className="icon-spinner2" />
                </div>
            );
        }

        return (
            <>
                {!isMobile ? (
                    <div className="details-header">
                        {this.renderEditMealServingsButton()}

                        <header>{this.renderControlBar()}</header>
                    </div>
                ) : null}

                <div className="meals-scroll-hero" data-index={0} data-singleton={false}>
                    <div className="hero-item" style={{ transform: 'translateX(30px)' }}>
                        <div className="hero-item-container">
                            <div className="meal-card" title={recipe.title}>
                                <div className="image-container recipe">
                                    <ImgResized
                                        alt={recipe.title}
                                        src={recipe.image}
                                        width={500}
                                        height={500}
                                        className="recipe-image"
                                    />
                                </div>

                                <div className="meal-info">
                                    <footer>
                                        <div className="lj">
                                            <div className="dish-badge">Dish 1</div>
                                        </div>
                                        <FavoriteButton recipe={recipe} />
                                    </footer>
                                    <p>{recipe.title}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="triangle" style={{ left: isMobile ? 65 : 140 }} />
                </div>

                <section className="details-section first">
                    <header>
                        <div className="section-title">
                            <div className="t2 with-merchant">
                                {merchant ? (
                                    <Popup
                                        positionClassName="el-popup-top-left-center"
                                        className="merchant-dropdown"
                                        button={
                                            <>
                                                Recipe by <em>{merchant.name}</em>{' '}
                                                <i className="feather feather-info" />
                                            </>
                                        }
                                    >
                                        <Merchant merchant={merchant} />
                                    </Popup>
                                ) : null}
                            </div>
                        </div>

                        <div className="meal-control-btns">
                            {canPublish ? (
                                <Link to={`/publisher/recipe/${recipe.uuid}`}>
                                    <button className="edit-meal-btn">
                                        <i className="feather feather-box" />
                                        <span>Admin</span>
                                    </button>
                                </Link>
                            ) : null}

                            <AddToCollectionButton recipe={recipe} />

                            <FavoriteButton icon="icon-heart2" iconBoarded="icon-heart" recipe={recipe}>
                                <span>Favorite</span>
                            </FavoriteButton>

                            {canEditSingleMeal && canDuplicateMeal ? (
                                <button className="edit-meal-btn" onClick={this.openRecipeDuplicateModal}>
                                    <i className="feather feather-copy" />
                                    <span>Duplicate</span>
                                </button>
                            ) : null}

                            {canEditSingleMeal ? (
                                <button className="edit-meal-btn" onClick={this.openRecipeEditModal}>
                                    <i className="feather feather-edit" />
                                    <span>Edit</span>
                                </button>
                            ) : null}
                        </div>
                    </header>

                    <div className="section-body">
                        <MealTitle item={recipe} readOnly />

                        <RecipeIngredients
                            recipe={recipe}
                            details={details}
                            subdetails={subdetails}
                            scaling={scaling}
                            profile={profile}
                        />
                    </div>
                </section>

                <section className="details-section second">
                    <header>
                        <div className="section-title">
                            <p className="t2">Dish Instructions</p>
                        </div>
                    </header>

                    <div className="section-body">
                        <RecipeInstructions recipe={recipe} details={details} subdetails={subdetails} user={user} />
                    </div>
                </section>

                {user && recipe.servings ? (
                    <div className="details-section third">
                        <header>
                            <div className="section-title">
                                <p className="t2">How Much to Eat</p>
                            </div>
                        </header>

                        <div className="section-body">
                            <HowMuchToEat
                                profile={profile}
                                recipe={recipe}
                                participants={participants}
                                scaling={scaling}
                                onClick={this.showEditMealServings}
                            />
                        </div>
                    </div>
                ) : null}

                {this.renderNutritionTabs()}
            </>
        );
    };

    renderSchedulingModal = () => {
        const { user, recipe, isScheduling, profile } = this.state;

        if (!isScheduling) {
            return null;
        }

        const { capabilities = {} } = user || {};
        const plans = PlanStore.getPlans();

        return (
            <SchedulingModal
                content={recipe}
                isLocked={recipe.protection !== 'public' && !capabilities.meal_planner}
                selectDate={this.addToCalendar}
                closeModal={this.closeScheduleModal}
                profile={profile || user}
                plans={plans}
            />
        );
    };

    renderLeftoverDatesModal = () => {
        const { leftoverParams, user, allMeals, allRecipes, allFoods, plans, recipe } = this.state;
        const { addSwapContext } = this.context;

        if (!leftoverParams) {
            return null;
        }

        // In add swap context, we only pass the temporary meal object, not all meals
        // This prevents LeftoverDatesModal from finding dishes from existing meals with same date/time
        const mealsForModal = addSwapContext && leftoverParams.meal
            ? [leftoverParams.meal]  // Only pass the temporary meal we created
            : allMeals || [];        // Normal flow: pass all meals

        // In add swap context, we need to pass the recipe as part of recipes
        const recipesForModal = addSwapContext && recipe
            ? { ...allRecipes, [recipe.uuid]: recipe }
            : allRecipes || {};

        return (
            <LeftoverDatesModal
                user={user}
                meals={mealsForModal}
                recipes={recipesForModal}
                foods={allFoods || {}}
                plans={plans || []}
                leftoverParams={leftoverParams}
                closeModal={this.closeLeftoverDatesModal}
                leftOverMealType={leftoverParams?.meal?.meal}
                onSelectLeftoverOffsets={this.onSelectLeftoverDates}
                returnMode={leftoverParams?.returnLeftoversOnly}
            />
        );
    };

    closeModal = () => {
        const { closeModal } = this.props;
        const { router } = this.context;

        if (closeModal) {
            return closeModal();
        }

        router.push('/');
    };

    onSaveRecipe = (recipe, details) => {
        const { onRecipeUpdated } = this.props;
        const { recipe: originalRecipe } = this.state;

        this.setState({ recipe, details }, this.syncAssets);

        // Notify parent component if recipe was updated
        if (onRecipeUpdated && originalRecipe) {
            onRecipeUpdated(originalRecipe.uuid, recipe);
        }

        this.closeRecipeEditModal();
    };

    renderRecipeEditModal = () => {
        const { recipe, profile, isEditModalOpen, saveAsCopy } = this.state;

        if (!isEditModalOpen) {
            return null;
        }

        return (
            <Modal
                isOpen={true}
                onRequestClose={this.closeRecipeEditModal}
                closeModal={this.closeRecipeEditModal}
                className="el-modal el-modal1"
                overlayClassName="el-modal-overlay"
                contentLabel="Edit Recipe Details"
            >
                <div className="el-modal-container el-modal1-container el-modal1-recipe-editor-container">
                    <header data-has-controls={false}>
                        <button className="el-modal-back-btn" onClick={this.closeRecipeEditModal}>
                            <span>Back</span>
                        </button>

                        <h2>Edit Recipe</h2>
                    </header>

                    <div className="el-modal-body-container el-modal1-body-container el-fonts">
                        <RecipeEditor
                            uuid={recipe.uuid}
                            saveAsCopy={saveAsCopy}
                            profile={profile}
                            closeModal={this.closeRecipeEditModal}
                            onSaveRecipe={this.onSaveRecipe}
                        />
                    </div>
                </div>
            </Modal>
        );
    };

    render() {
        const { isMobile } = this.context;
        const { profile, recipe } = this.state;

        if (!profile) {
            return (
                <div className="my-account-login">
                    <Helmet title="Please sign-in or register | EatLove" />

                    <LoginForm defaultSignUp={false} />
                    <Footer />
                </div>
            );
        }

        return (
            <>
                {recipe ? (
                    <Helmet
                        title={recipe.title + ' | EatLove'}
                        meta={[
                            { name: 'description', content: recipe.description },
                            { name: 'twitter:title', content: recipe.title },
                            { name: 'twitter:description', content: recipe.description },
                            { name: 'twitter:image', content: recipe.image },
                            { property: 'og:title', content: recipe.title },
                            { property: 'og:type', content: recipe.page_type },
                            {
                                property: 'og:url',
                                content: 'https://' + getConfig('self_host') + '/recipes/' + recipe.uuid,
                            },
                            { property: 'og:image', content: recipe.image },
                            { property: 'og:description', content: recipe.description },
                        ]}
                    />
                ) : null}
                <Modal
                    isOpen={true}
                    onRequestClose={this.closeModal}
                    closeModal={this.closeModal}
                    className="el-modal el-modal1"
                    overlayClassName="el-modal-overlay"
                    contentLabel="Meal Details"
                >
                    <div className="el-modal-container el-modal1-container content-details-modal-container">
                        <header data-has-controls={isMobile}>
                            <button className="el-modal-back-btn" onClick={this.closeModal}>
                                <span>Back</span>
                            </button>

                            <h2>Recipe</h2>

                            {isMobile ? (
                                <>
                                    {this.renderEditMealServingsButton()}

                                    <div className="el-modal-controls el-modal1-controls">
                                        <header>{this.renderControlBar()}</header>
                                    </div>
                                </>
                            ) : null}
                        </header>

                        <div className="el-modal-body-container el-modal1-body-container el-fonts">
                            {this.renderModalBody()}
                        </div>
                    </div>
                </Modal>

                {this.renderEditMealServingsModal()}
                {this.renderSchedulingModal()}
                {this.renderLeftoverDatesModal()}
                {this.renderRecipeEditModal()}
                {this.renderRecommendModal()}
                {this.renderConfirmOverwriteModal()}
            </>
        );
    }
}
